import { CORES_SEMANTICAS } from './tokens'

/**
 * Classes de resistência do aço da carroceria.
 *
 * Existe para o módulo de técnicas de corte: o que decide se uma coluna cede,
 * resiste ou exige outra ferramenta não é a espessura aparente da chapa, é a
 * classe do aço — e ela não se vê por fora.
 *
 * A nomenclatura é a usada nos mapas de carroceria em português, e não a sigla
 * inglesa, para casar com o material de aula.
 *
 * ATENÇÃO, e isto vale para todo consumo deste módulo: a classe atribuída a uma
 * peça descreve a **construção provável desta maquete didática**, nunca um
 * veículo real. Dois carros do mesmo segmento podem diferir, e o mesmo modelo
 * muda entre gerações. Na ocorrência, o que vale é a ficha de salvamento do
 * veículo. Um aluno que sair daqui achando que "coluna B é sempre boro"
 * aprendeu errado, e no corte esse erro custa caro.
 */
export type ClasseAco =
  | 'estampagemProfunda'
  | 'media'
  | 'alta'
  | 'ultraAlta'
  | 'boro'
  | 'aluminio'

export interface DescricaoClasseAco {
  nome: string
  cor: string
  /** o que a classe significa para quem vai cortar */
  leituraOperacional: string
}

/**
 * Cores das classes.
 *
 * Reusam os valores já validados da paleta semântica em vez de trazer uma
 * paleta nova: o aluno já associa âmbar a "atenção" e roxo a "eixo", e a camada
 * de aço só aparece quando ele a liga, sem competir com as outras.
 */
export const CLASSES_ACO: Record<ClasseAco, DescricaoClasseAco> = {
  estampagemProfunda: {
    nome: 'Estampagem profunda',
    cor: '#c8cfdb',
    leituraOperacional:
      'Chapa conformável, de fechamento e acabamento. Cede com facilidade e raramente é o que segura a estrutura.',
  },
  media: {
    nome: 'Média resistência',
    cor: CORES_SEMANTICAS.estrutura,
    leituraOperacional:
      'Resiste mais que a chapa comum, mas ainda é o que se espera que deforme para absorver energia.',
  },
  alta: {
    nome: 'Alta resistência',
    cor: CORES_SEMANTICAS.srs,
    leituraOperacional:
      'Elemento que preserva forma. Exige ferramenta em boas condições e escolha cuidadosa do ponto de corte.',
  },
  ultraAlta: {
    nome: 'Ultra-alta resistência',
    cor: CORES_SEMANTICAS.zonaDeformacao,
    leituraOperacional:
      'Projetado para não ceder. Corte fora dele quando houver alternativa, e conte com esforço maior quando não houver.',
  },
  boro: {
    nome: 'Aço do tipo boro',
    cor: CORES_SEMANTICAS.eixoAnatomico,
    leituraOperacional:
      'A classe mais dura da carroceria. Pode exceder a capacidade de ferramenta convencional; confirme na ficha antes de escolher o ponto.',
  },
  aluminio: {
    nome: 'Alumínio',
    cor: CORES_SEMANTICAS.trajetoria,
    leituraOperacional:
      'Comporta-se de forma diferente do aço: rompe em vez de dobrar, e não responde a ímã nem a técnica pensada para aço.',
  },
}

/** Ordem de apresentação: da chapa que cede à que não cede. */
export const ORDEM_CLASSES_ACO: ClasseAco[] = [
  'estampagemProfunda',
  'media',
  'alta',
  'ultraAlta',
  'boro',
  'aluminio',
]

export const corDaClasseAco = (classe: ClasseAco): string => CLASSES_ACO[classe].cor
